/**
 * LotteryCampaignDetailModal — جزئیات کامل کمپین قرعه‌کشی مایلستونی
 *
 * تب‌ها: نمای کلی (آمار + مایلستون‌ها + جوایز)، شرکت‌کنندگان (با نشان‌گذاری تقلب)،
 * مدارک (بررسی مدارک شرط دلخواه)، قرعه‌کشی (commit-reveal اثبات‌پذیر + تحویل جوایز).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupsIcon from '@mui/icons-material/Groups';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import LinkIcon from '@mui/icons-material/Link';
import FlagIcon from '@mui/icons-material/Flag';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LockIcon from '@mui/icons-material/Lock';
import CasinoIcon from '@mui/icons-material/Casino';
import RedeemIcon from '@mui/icons-material/Redeem';
import { useSnackbar } from 'notistack';
import {
  ResponsiveModal,
  ConfirmDialog,
  StatsCard,
  CopyableText,
  EmptyState,
  ResponsiveTable,
  Column,
} from '../common';
import { toPersianDigits } from '../common/JalaliDatePicker';
import { formatDate } from '../../utils/dateUtils';
import lotteryService, {
  AGGREGATE_LABELS,
  CAMPAIGN_STATUS_COLORS,
  CAMPAIGN_STATUS_LABELS,
  ENTRY_MODE_LABELS,
  EVENT_LABELS,
  LotteryCampaignDetail,
  LotteryCampaignStatus,
  LotteryDrawDoc,
  LotteryProgressItem,
  LotteryProofItem,
  PRIZE_TYPE_LABELS,
  SCOPE_LABELS,
  userDisplayName,
} from '../../services/lottery.service';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

export const CampaignStatusChip: React.FC<{ status: LotteryCampaignStatus }> = ({ status }) => (
  <Chip
    size="small"
    label={CAMPAIGN_STATUS_LABELS[status] || status}
    color={CAMPAIGN_STATUS_COLORS[status] || 'default'}
    variant={status === 'draft' || status === 'cancelled' ? 'outlined' : 'filled'}
  />
);

const faNum = (n: number | string | undefined | null): string =>
  toPersianDigits(String(n ?? 0));

const toman = (n?: number | null): string =>
  typeof n === 'number' && !Number.isNaN(n) ? new Intl.NumberFormat('fa-IR').format(n) : '-';

interface Props {
  campaignId: string | null;
  open: boolean;
  onClose: () => void;
  /** بعد از هر تغییری که وضعیت کمپین را عوض می‌کند صدا زده می‌شود (برای رفرش لیست) */
  onChanged?: () => void;
}

const LotteryCampaignDetailModal: React.FC<Props> = ({ campaignId, open, onClose, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [tab, setTab] = useState(0);
  const [detail, setDetail] = useState<LotteryCampaignDetail | null>(null);
  const [loading, setLoading] = useState(false);

  // --- Participants tab state ---
  const [participants, setParticipants] = useState<LotteryProgressItem[]>([]);
  const [pLoading, setPLoading] = useState(false);
  const [pPage, setPPage] = useState(1);
  const [pTotal, setPTotal] = useState(0);
  const [pLimit] = useState(20);
  const [suspectOnly, setSuspectOnly] = useState(false);

  // Flag-fraud dialog
  const [flagTarget, setFlagTarget] = useState<{ userId: string; name: string; suspect: boolean } | null>(null);
  const [flagReason, setFlagReason] = useState('');
  const [flagLoading, setFlagLoading] = useState(false);

  // --- Proofs tab state ---
  const [proofs, setProofs] = useState<LotteryProofItem[]>([]);
  const [proofsLoading, setProofsLoading] = useState(false);
  const [proofsPage, setProofsPage] = useState(1);
  const [proofsTotal, setProofsTotal] = useState(0);
  const [rejectProofTarget, setRejectProofTarget] = useState<LotteryProofItem | null>(null);
  const [proofRejectReason, setProofRejectReason] = useState('');
  const [proofActionLoading, setProofActionLoading] = useState(false);

  // --- Draw tab state ---
  const [drawActionLoading, setDrawActionLoading] = useState(false);
  const [confirmCommit, setConfirmCommit] = useState(false);
  const [confirmExecute, setConfirmExecute] = useState(false);
  const [fulfillTarget, setFulfillTarget] = useState<{ rank: number; prizeTitle: string; userName: string } | null>(null);
  const [fulfillNote, setFulfillNote] = useState('');

  const campaign = detail?.campaign || null;
  const draw = detail?.draw || null;

  // ------------------------------------------------------------------ loads

  const loadDetail = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const d = await lotteryService.getCampaignDetail(campaignId);
      setDetail(d);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در دریافت جزئیات کمپین', { variant: 'error' });
      onClose();
    } finally {
      setLoading(false);
    }
  }, [campaignId, enqueueSnackbar, onClose]);

  useEffect(() => {
    if (open && campaignId) {
      setTab(0);
      setDetail(null);
      setPPage(1);
      setSuspectOnly(false);
      setProofsPage(1);
      loadDetail();
    }
  }, [open, campaignId, loadDetail]);

  const loadParticipants = useCallback(async () => {
    if (!campaignId) return;
    setPLoading(true);
    try {
      const res = await lotteryService.getParticipants(campaignId, {
        page: pPage,
        limit: pLimit,
        suspect: suspectOnly || undefined,
      });
      setParticipants(res.items);
      setPTotal(res.meta.totalItems);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در دریافت شرکت‌کنندگان', { variant: 'error' });
    } finally {
      setPLoading(false);
    }
  }, [campaignId, pPage, pLimit, suspectOnly, enqueueSnackbar]);

  useEffect(() => {
    if (open && tab === 1) loadParticipants();
  }, [open, tab, loadParticipants]);

  const loadProofs = useCallback(async () => {
    if (!campaignId) return;
    setProofsLoading(true);
    try {
      const res = await lotteryService.getProofs({
        status: 'pending',
        campaignId,
        page: proofsPage,
        limit: 20,
      });
      setProofs(res.items);
      setProofsTotal(res.meta.totalItems);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در دریافت مدارک', { variant: 'error' });
    } finally {
      setProofsLoading(false);
    }
  }, [campaignId, proofsPage, enqueueSnackbar]);

  useEffect(() => {
    if (open && tab === 2) loadProofs();
  }, [open, tab, loadProofs]);

  // ------------------------------------------------------------- flag fraud

  const runFlagFraud = async () => {
    if (!campaignId || !flagTarget) return;
    if (flagTarget.suspect && !flagReason.trim()) {
      enqueueSnackbar('نوشتن دلیل نشان‌گذاری الزامی است', { variant: 'warning' });
      return;
    }
    setFlagLoading(true);
    try {
      await lotteryService.flagFraud(
        campaignId,
        flagTarget.userId,
        flagTarget.suspect,
        flagReason.trim() || undefined
      );
      enqueueSnackbar(
        flagTarget.suspect ? 'کاربر به‌عنوان مشکوک نشان‌گذاری شد' : 'نشان تقلب کاربر برداشته شد',
        { variant: 'success' }
      );
      setFlagTarget(null);
      setFlagReason('');
      await loadParticipants();
      loadDetail();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در نشان‌گذاری', { variant: 'error' });
    } finally {
      setFlagLoading(false);
    }
  };

  // ----------------------------------------------------------------- proofs

  const milestoneTitleByKey = useMemo(() => {
    const map: Record<string, string> = {};
    (campaign?.milestones || []).forEach((m) => {
      if (m.key) map[m.key] = m.title;
    });
    return map;
  }, [campaign]);

  const approveProof = async (proof: LotteryProofItem) => {
    setProofActionLoading(true);
    try {
      await lotteryService.reviewProof(proof._id, true);
      enqueueSnackbar('مدرک تایید شد', { variant: 'success' });
      await loadProofs();
      loadDetail();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در تایید مدرک', { variant: 'error' });
    } finally {
      setProofActionLoading(false);
    }
  };

  const runRejectProof = async () => {
    if (!rejectProofTarget) return;
    if (!proofRejectReason.trim()) {
      enqueueSnackbar('نوشتن دلیل رد الزامی است', { variant: 'warning' });
      return;
    }
    setProofActionLoading(true);
    try {
      await lotteryService.reviewProof(rejectProofTarget._id, false, proofRejectReason.trim());
      enqueueSnackbar('مدرک رد شد', { variant: 'success' });
      setRejectProofTarget(null);
      setProofRejectReason('');
      await loadProofs();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در رد مدرک', { variant: 'error' });
    } finally {
      setProofActionLoading(false);
    }
  };

  // ------------------------------------------------------------------- draw

  const canCommit =
    !!campaign &&
    !draw &&
    (campaign.status === 'ended' ||
      (campaign.status === 'active' && new Date(campaign.endAt).getTime() < Date.now()));

  const runCommit = async () => {
    if (!campaignId) return;
    setDrawActionLoading(true);
    try {
      const res = await lotteryService.commitDraw(campaignId);
      enqueueSnackbar('تعهد قرعه‌کشی ثبت شد؛ هش seed منتشر شد', { variant: 'success' });
      setConfirmCommit(false);
      setDetail((prev) =>
        prev
          ? {
              ...prev,
              campaign: { ...prev.campaign, status: 'drawing' },
              draw: { seedHash: res.seedHash, winners: [] },
            }
          : prev
      );
      onChanged?.();
      loadDetail();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در ثبت تعهد قرعه‌کشی', { variant: 'error' });
    } finally {
      setDrawActionLoading(false);
    }
  };

  const runExecute = async () => {
    if (!campaignId) return;
    setDrawActionLoading(true);
    try {
      const d = await lotteryService.executeDraw(campaignId);
      enqueueSnackbar('قرعه‌کشی اجرا شد و برنده‌ها مشخص شدند', { variant: 'success' });
      setConfirmExecute(false);
      setDetail((prev) =>
        prev ? { ...prev, campaign: { ...prev.campaign, status: 'finished' }, draw: d } : prev
      );
      onChanged?.();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در اجرای قرعه‌کشی', { variant: 'error' });
    } finally {
      setDrawActionLoading(false);
    }
  };

  const runFulfill = async () => {
    if (!campaignId || !fulfillTarget) return;
    setDrawActionLoading(true);
    try {
      await lotteryService.fulfillWinner(campaignId, fulfillTarget.rank, fulfillNote.trim() || undefined);
      enqueueSnackbar('جایزه به‌عنوان تحویل‌شده ثبت شد', { variant: 'success' });
      // پاسخ سرور userId برنده‌ها را populate نمی‌کند؛ فقط فیلدهای تحویل را ادغام می‌کنیم
      setDetail((prev) => {
        if (!prev?.draw) return prev;
        const winners = prev.draw.winners.map((w) =>
          w.rank === fulfillTarget.rank
            ? { ...w, fulfilled: true, fulfilledAt: new Date().toISOString(), fulfillNote: fulfillNote.trim() || w.fulfillNote }
            : w
        );
        return { ...prev, draw: { ...prev.draw, winners } as LotteryDrawDoc };
      });
      setFulfillTarget(null);
      setFulfillNote('');
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در ثبت تحویل جایزه', { variant: 'error' });
    } finally {
      setDrawActionLoading(false);
    }
  };

  // ---------------------------------------------------------------- renders

  const renderOverview = () => {
    if (!campaign || !detail) return null;
    const store: any = campaign.storeId;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* آمار */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
            gap: 1.5,
          }}
        >
          <StatsCard title="شرکت‌کننده" value={detail.stats.participants} icon={<GroupsIcon />} iconBgColor="#B25373" />
          <StatsCard title="بلیت" value={detail.stats.tickets} icon={<ConfirmationNumberIcon />} iconBgColor="#7c3aed" />
          <StatsCard title="تکمیل‌کرده" value={detail.stats.completed} icon={<TaskAltIcon />} iconBgColor="#16a34a" />
          <StatsCard title="مشکوک" value={detail.stats.suspects} icon={<ReportProblemIcon />} iconBgColor="#dc2626" />
          <StatsCard title="بازدید دعوت" value={detail.stats.referralVisits} icon={<LinkIcon />} iconBgColor="#ea580c" />
        </Box>

        {/* اطلاعات کمپین */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            اطلاعات کمپین
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 1.25,
              '& .k': { color: 'text.secondary', fontSize: 12 },
              '& .v': { fontSize: 13, fontWeight: 500 },
            }}
          >
            <Box><div className="k">وضعیت</div><div className="v"><CampaignStatusChip status={campaign.status} /></div></Box>
            <Box><div className="k">محدوده</div><div className="v">{SCOPE_LABELS[campaign.scope] || campaign.scope}{store?.name ? ` — ${store.name}` : ''}</div></Box>
            <Box><div className="k">شروع</div><div className="v">{formatDate(campaign.startAt)}</div></Box>
            <Box><div className="k">پایان</div><div className="v">{formatDate(campaign.endAt)}</div></Box>
            <Box><div className="k">شیوه بلیت‌دهی</div><div className="v">{ENTRY_MODE_LABELS[campaign.entryMode] || campaign.entryMode}</div></Box>
            <Box><div className="k">سازنده</div><div className="v">{userDisplayName(campaign.createdBy as any)}</div></Box>
            <Box>
              <div className="k">شرایط شرکت</div>
              <div className="v">
                {campaign.eligibility?.requirePhoneVerified ? 'موبایل تاییدشده' : 'بدون نیاز به تایید موبایل'}
                {campaign.eligibility?.minAccountAgeDays
                  ? ` — حداقل عمر حساب ${faNum(campaign.eligibility.minAccountAgeDays)} روز`
                  : ''}
              </div>
            </Box>
            <Box><div className="k">اسلاگ</div><div className="v"><CopyableText value={campaign.slug} mono /></div></Box>
          </Box>
          {campaign.description && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.9 }}>
                {campaign.description}
              </Typography>
            </>
          )}
          {campaign.status === 'rejected' && campaign.rejectionReason && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              دلیل رد: {campaign.rejectionReason}
            </Alert>
          )}
        </Paper>

        {/* مایلستون‌ها */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            مایلستون‌ها ({faNum(campaign.milestones?.length || 0)})
          </Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>عنوان</TableCell>
                  <TableCell>رویداد</TableCell>
                  <TableCell>سنجه</TableCell>
                  <TableCell>هدف</TableCell>
                  <TableCell>تکرارپذیر</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(campaign.milestones || []).map((m, i) => (
                  <TableRow key={m.key || i}>
                    <TableCell>
                      {m.title}
                      {m.event === 'manual' && m.manualInstructions && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {m.manualInstructions}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{EVENT_LABELS[m.event] || m.event}</TableCell>
                    <TableCell>{AGGREGATE_LABELS[m.aggregate] || m.aggregate}</TableCell>
                    <TableCell>
                      {m.aggregate === 'sum_amount' ? `${toman(m.target)} تومان` : faNum(m.target)}
                      {m.filters?.minAmount ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          حداقل مبلغ: {toman(m.filters.minAmount)} تومان
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {m.repeatable
                        ? `بله${m.maxRepeats ? ` (حداکثر ${faNum(m.maxRepeats)})` : ''}`
                        : 'خیر'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* جوایز */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            جوایز ({faNum(campaign.prizes?.length || 0)}) — ترتیب جوایز همان رتبه برنده‌هاست
          </Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>رتبه</TableCell>
                  <TableCell>عنوان</TableCell>
                  <TableCell>نوع</TableCell>
                  <TableCell>مبلغ / توضیح</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(campaign.prizes || []).map((p, i) => (
                  <TableRow key={i}>
                    <TableCell>{faNum(i + 1)}</TableCell>
                    <TableCell>{p.title}</TableCell>
                    <TableCell>{PRIZE_TYPE_LABELS[p.type] || p.type}</TableCell>
                    <TableCell>
                      {p.type === 'wallet_credit' && p.amount ? `${toman(p.amount)} تومان` : p.description || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  };

  const participantColumns: Column<LotteryProgressItem>[] = [
    {
      id: 'userId',
      label: 'کاربر',
      priority: 1,
      format: (_v, row) => {
        const u: any = row.userId;
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {userDisplayName(u)}
            </Typography>
            {typeof u === 'object' && u?.phone && (
              <Typography variant="caption" color="text.secondary" dir="ltr">
                {toPersianDigits(u.phone)}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      id: 'ticketCount',
      label: 'بلیت',
      priority: 2,
      format: (v) => <Chip size="small" color="primary" variant="outlined" label={faNum(v || 0)} />,
    },
    {
      id: 'allCompletedAt',
      label: 'تکمیل همه مایلستون‌ها',
      hideOnMobile: true,
      format: (v) => (v ? formatDate(v) : '—'),
    },
    {
      id: 'referralStats',
      label: 'آمار دعوت (بازدید/ثبت‌نام/خرید)',
      detail: true,
      format: (_v, row) =>
        `${faNum(row.referralStats?.visits || 0)} / ${faNum(row.referralStats?.signups || 0)} / ${faNum(row.referralStats?.purchases || 0)}`,
    },
    {
      id: 'milestones',
      label: 'پیشرفت مایلستون‌ها',
      detail: true,
      format: (_v, row) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
          {(row.milestones || []).map((m) => (
            <Chip
              key={m.key}
              size="small"
              variant="outlined"
              color={m.completedCount > 0 ? 'success' : 'default'}
              label={`${milestoneTitleByKey[m.key] || m.key}: ${faNum(m.completedCount)}`}
            />
          ))}
        </Box>
      ),
    },
    {
      id: 'fraudSuspect',
      label: 'وضعیت',
      priority: 3,
      format: (_v, row) =>
        row.fraudSuspect ? (
          <Tooltip title={row.fraudReason || ''} arrow>
            <Chip size="small" color="error" icon={<FlagIcon />} label="مشکوک به تقلب" />
          </Tooltip>
        ) : (
          <Chip size="small" variant="outlined" label="عادی" />
        ),
    },
  ];

  const renderParticipants = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={suspectOnly}
              onChange={(e) => {
                setSuspectOnly(e.target.checked);
                setPPage(1);
              }}
            />
          }
          label={<Typography variant="body2">فقط مشکوک به تقلب</Typography>}
        />
        <Typography variant="caption" color="text.secondary">
          مجموع: {faNum(pTotal)} شرکت‌کننده
        </Typography>
      </Box>
      <ResponsiveTable<LotteryProgressItem>
        columns={participantColumns}
        data={participants}
        loading={pLoading}
        keyField="_id"
        emptyMessage="شرکت‌کننده‌ای یافت نشد"
        pagination
        page={pPage - 1}
        rowsPerPage={pLimit}
        totalCount={pTotal}
        onPageChange={(p) => setPPage(p + 1)}
        rowsPerPageOptions={[20]}
        renderActions={(row) => {
          const u: any = row.userId;
          const userId = typeof u === 'object' ? u?._id : u;
          const name = userDisplayName(u);
          return row.fraudSuspect ? (
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<OutlinedFlagIcon />}
              onClick={() => {
                setFlagTarget({ userId, name, suspect: false });
                setFlagReason('');
              }}
            >
              رفع نشان تقلب
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<FlagIcon />}
              onClick={() => {
                setFlagTarget({ userId, name, suspect: true });
                setFlagReason('');
              }}
            >
              نشان‌گذاری تقلب
            </Button>
          );
        }}
      />
    </Box>
  );

  const renderProofs = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Alert severity="info" icon={false}>
        مدارک در انتظار بررسی برای مایلستون‌های «شرط دلخواه با مدرک». با تایید مدرک، مایلستون برای کاربر
        تکمیل می‌شود.
      </Alert>
      {proofsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={30} />
        </Box>
      ) : proofs.length === 0 ? (
        <EmptyState variant="empty" title="مدرکی در انتظار بررسی نیست" dense />
      ) : (
        proofs.map((proof) => {
          const u: any = proof.userId;
          return (
            <Paper key={proof._id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {proof.imageUrl && (
                  <Box
                    component="a"
                    href={proof.imageUrl}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ flexShrink: 0 }}
                    title="مشاهده تصویر در اندازه کامل"
                  >
                    <Box
                      component="img"
                      src={proof.imageUrl}
                      alt="مدرک"
                      sx={{
                        width: 110,
                        height: 110,
                        objectFit: 'cover',
                        borderRadius: 1.5,
                        border: (t) => `1px solid ${t.palette.divider}`,
                      }}
                    />
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {userDisplayName(u)}
                    {typeof u === 'object' && u?.phone ? (
                      <Typography component="span" variant="caption" color="text.secondary" dir="ltr" sx={{ mx: 1 }}>
                        {toPersianDigits(u.phone)}
                      </Typography>
                    ) : null}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    مایلستون: {milestoneTitleByKey[proof.milestoneKey] || proof.milestoneKey}
                    {proof.createdAt ? ` — ${formatDate(proof.createdAt)}` : ''}
                  </Typography>
                  {proof.note && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                      {proof.note}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      disabled={proofActionLoading}
                      onClick={() => approveProof(proof)}
                    >
                      تایید مدرک
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      disabled={proofActionLoading}
                      onClick={() => {
                        setRejectProofTarget(proof);
                        setProofRejectReason('');
                      }}
                    >
                      رد مدرک
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Paper>
          );
        })
      )}
      {proofsTotal > 20 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <Button size="small" disabled={proofsPage <= 1} onClick={() => setProofsPage((p) => p - 1)}>
            قبلی
          </Button>
          <Button
            size="small"
            disabled={proofsPage >= Math.ceil(proofsTotal / 20)}
            onClick={() => setProofsPage((p) => p + 1)}
          >
            بعدی
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderDraw = () => {
    if (!campaign) return null;
    const revealed = !!draw?.revealedAt || (draw?.winners?.length || 0) > 0;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="info">
          قرعه‌کشی به روش <strong>commit-reveal</strong> انجام می‌شود تا اثبات‌پذیر و منصفانه باشد: ابتدا هش
          seed تصادفی منتشر می‌شود (تعهد)، سپس در مرحله اجرا خود seed آشکار شده و برنده‌ها به‌صورت قطعی از
          روی آن انتخاب می‌شوند. هر کس می‌تواند با seed و الگوریتم اعلام‌شده نتیجه را بازتولید و راستی‌آزمایی
          کند.
        </Alert>

        {/* مرحله ۱: commit */}
        {!draw && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              مرحله ۱ — ثبت تعهد (commit)
            </Typography>
            {canCommit ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.9 }}>
                  با ثبت تعهد، seed تصادفی تولید و هش آن منتشر می‌شود؛ از این لحظه بلیت جدیدی صادر نمی‌شود و
                  کمپین به وضعیت «در حال قرعه‌کشی» می‌رود.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<LockIcon />}
                  disabled={drawActionLoading}
                  onClick={() => setConfirmCommit(true)}
                >
                  ثبت تعهد (commit)
                </Button>
              </>
            ) : (
              <Alert severity="warning" icon={false}>
                قرعه‌کشی فقط بعد از پایان بازه کمپین ممکن است (وضعیت فعلی:{' '}
                {CAMPAIGN_STATUS_LABELS[campaign.status]}).
              </Alert>
            )}
          </Paper>
        )}

        {/* seedHash + مرحله ۲ */}
        {draw && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              تعهد ثبت‌شده
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
                  هش seed:
                </Typography>
                <CopyableText value={draw.seedHash} mono maxWidth={280} />
              </Box>
              {revealed && draw.seed && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
                    seed آشکارشده:
                  </Typography>
                  <CopyableText value={draw.seed} mono maxWidth={280} />
                </Box>
              )}
              {revealed && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
                    الگوریتم:
                  </Typography>
                  <CopyableText value={draw.algorithm || 'hmac-sha256-v1'} mono />
                  <Typography variant="caption" color="text.secondary">
                    — تعداد بلیت: {faNum(draw.ticketCount || 0)}
                  </Typography>
                </Box>
              )}
            </Box>

            {!revealed && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  مرحله ۲ — اجرای قرعه‌کشی (reveal)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.9 }}>
                  با اجرا، seed آشکار و برنده‌ها به‌صورت قطعی انتخاب می‌شوند. کاربران مشکوک به تقلب از
                  قرعه‌کشی حذف می‌شوند و هر کاربر حداکثر یک جایزه می‌برد.
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<CasinoIcon />}
                  disabled={drawActionLoading}
                  onClick={() => setConfirmExecute(true)}
                >
                  اجرای قرعه‌کشی
                </Button>
              </>
            )}
          </Paper>
        )}

        {/* برنده‌ها */}
        {revealed && draw && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              برنده‌ها ({faNum(draw.winners.length)})
            </Typography>
            <Alert severity="info" icon={false} sx={{ mb: 1.5 }}>
              جوایز «شارژ کیف پول» هنگام ثبت تحویل به‌صورت خودکار به کیف پول برنده واریز می‌شوند.
            </Alert>
            {draw.winners.length === 0 ? (
              <EmptyState variant="empty" title="برنده‌ای انتخاب نشد (بلیتی وجود نداشت)" dense />
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>رتبه</TableCell>
                      <TableCell>جایزه</TableCell>
                      <TableCell>برنده</TableCell>
                      <TableCell>شماره بلیت</TableCell>
                      <TableCell>تحویل</TableCell>
                      <TableCell align="center">عملیات</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {draw.winners.map((w) => {
                      const u: any = w.userId;
                      const isPopulated = typeof u === 'object' && u;
                      return (
                        <TableRow key={w.rank}>
                          <TableCell>{faNum(w.rank)}</TableCell>
                          <TableCell>{w.prizeTitle}</TableCell>
                          <TableCell>
                            {isPopulated ? (
                              userDisplayName(u)
                            ) : (
                              <CopyableText value={String(u)} mono maxWidth={120} />
                            )}
                          </TableCell>
                          <TableCell>{faNum(w.ticketSeq)}</TableCell>
                          <TableCell>
                            {w.fulfilled ? (
                              <Tooltip title={w.fulfillNote || ''} arrow>
                                <Chip
                                  size="small"
                                  color="success"
                                  icon={<CheckCircleIcon />}
                                  label={w.fulfilledAt ? formatDate(w.fulfilledAt) : 'تحویل شد'}
                                />
                              </Tooltip>
                            ) : (
                              <Chip size="small" variant="outlined" color="warning" label="در انتظار تحویل" />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {!w.fulfilled && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<RedeemIcon />}
                                disabled={drawActionLoading}
                                onClick={() => {
                                  setFulfillTarget({
                                    rank: w.rank,
                                    prizeTitle: w.prizeTitle,
                                    userName: isPopulated ? userDisplayName(u) : String(u),
                                  });
                                  setFulfillNote('');
                                }}
                              >
                                ثبت تحویل
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}
      </Box>
    );
  };

  return (
    <>
      <ResponsiveModal
        open={open}
        onClose={onClose}
        title={campaign ? campaign.title : 'جزئیات کمپین قرعه‌کشی'}
        subtitle={campaign ? `${SCOPE_LABELS[campaign.scope]} — ${CAMPAIGN_STATUS_LABELS[campaign.status]}` : undefined}
        icon={<EmojiEventsIcon />}
        maxWidth="lg"
        mobileBreakpoint="md"
        loading={loading && !detail}
        id="lottery-detail"
      >
        {detail && (
          <Box>
            <Tabs
              value={tab}
              onChange={(_e, v) => setTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{ mb: 2, borderBottom: (t) => `1px solid ${t.palette.divider}` }}
            >
              <Tab label="نمای کلی" />
              <Tab label="شرکت‌کنندگان" />
              <Tab label="مدارک" />
              <Tab label="قرعه‌کشی" />
            </Tabs>
            {tab === 0 && renderOverview()}
            {tab === 1 && renderParticipants()}
            {tab === 2 && renderProofs()}
            {tab === 3 && renderDraw()}
          </Box>
        )}
      </ResponsiveModal>

      {/* نشان‌گذاری/رفع نشان تقلب */}
      <ConfirmDialog
        open={!!flagTarget}
        title={flagTarget?.suspect ? 'نشان‌گذاری مشکوک به تقلب' : 'رفع نشان تقلب'}
        message={
          flagTarget?.suspect
            ? `کاربر «${flagTarget?.name}» به‌عنوان مشکوک به تقلب نشان‌گذاری می‌شود و از قرعه‌کشی حذف خواهد شد. دلیل را بنویسید:`
            : `نشان تقلب کاربر «${flagTarget?.name}» برداشته می‌شود و دوباره در قرعه‌کشی شرکت داده خواهد شد. ادامه می‌دهید؟`
        }
        confirmText={flagTarget?.suspect ? 'نشان‌گذاری' : 'رفع نشان'}
        confirmColor={flagTarget?.suspect ? 'error' : 'success'}
        loading={flagLoading}
        onCancel={() => setFlagTarget(null)}
        onConfirm={runFlagFraud}
      >
        {flagTarget?.suspect && (
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            autoFocus
            placeholder="دلیل نشان‌گذاری (الزامی)..."
            value={flagReason}
            onChange={(e) => setFlagReason(e.target.value)}
          />
        )}
      </ConfirmDialog>

      {/* رد مدرک */}
      <ConfirmDialog
        open={!!rejectProofTarget}
        title="رد مدرک"
        message="مدرک رد می‌شود و کاربر دلیل آن را می‌بیند. لطفا دلیل رد را بنویسید:"
        confirmText="رد مدرک"
        confirmColor="error"
        loading={proofActionLoading}
        onCancel={() => setRejectProofTarget(null)}
        onConfirm={runRejectProof}
      >
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          autoFocus
          placeholder="دلیل رد (الزامی)..."
          value={proofRejectReason}
          onChange={(e) => setProofRejectReason(e.target.value)}
        />
      </ConfirmDialog>

      {/* تایید commit */}
      <ConfirmDialog
        open={confirmCommit}
        title="ثبت تعهد قرعه‌کشی"
        message="با ثبت تعهد، هش seed منتشر می‌شود و امکان صدور بلیت جدید از بین می‌رود. این عملیات قابل بازگشت نیست. ادامه می‌دهید؟"
        confirmText="ثبت تعهد"
        confirmColor="primary"
        destructive={false}
        loading={drawActionLoading}
        onCancel={() => setConfirmCommit(false)}
        onConfirm={runCommit}
      />

      {/* تایید execute */}
      <ConfirmDialog
        open={confirmExecute}
        title="اجرای قرعه‌کشی"
        message="با اجرا، seed آشکار و برنده‌ها به‌صورت قطعی و غیرقابل تغییر انتخاب می‌شوند. ادامه می‌دهید؟"
        confirmText="اجرای قرعه‌کشی"
        confirmColor="secondary"
        destructive={false}
        loading={drawActionLoading}
        onCancel={() => setConfirmExecute(false)}
        onConfirm={runExecute}
      />

      {/* ثبت تحویل جایزه */}
      <ConfirmDialog
        open={!!fulfillTarget}
        title="ثبت تحویل جایزه"
        message={`جایزه «${fulfillTarget?.prizeTitle}» (رتبه ${faNum(fulfillTarget?.rank || 0)}) برای «${fulfillTarget?.userName}» به‌عنوان تحویل‌شده ثبت می‌شود. اگر جایزه از نوع شارژ کیف پول باشد، مبلغ به‌صورت خودکار واریز می‌شود.`}
        confirmText="ثبت تحویل"
        confirmColor="success"
        destructive={false}
        loading={drawActionLoading}
        onCancel={() => setFulfillTarget(null)}
        onConfirm={runFulfill}
      >
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          placeholder="یادداشت تحویل (اختیاری)..."
          value={fulfillNote}
          onChange={(e) => setFulfillNote(e.target.value)}
        />
      </ConfirmDialog>
    </>
  );
};

export default LotteryCampaignDetailModal;
